import { Plus, Search } from "lucide-react-native";

import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Stack } from "@/components/atoms/Stack";

/**
 *
 */
interface TagSelectorSearchSectionProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  canCreateNew: boolean;
  onCreateFromSearch: () => void;
}

/**
 * TagSelectorSearchSection View Component
 * Pure UI component for search input and create-from-search functionality.
 *
 * Chrome is the board's controls-row idiom (SE-6554): a 36px-tall outlined
 * field on `outline-strong`, radius 4, with a muted leading magnifier. The
 * Input atom already supplies `text-content-primary` and
 * `placeholder:text-content-muted`, so no colour is spelled here — the field
 * previously forced a raw dark slate fill, a fixed light label colour and a
 * literal hex placeholder, which rendered a dark slab in light theme.
 * @param root0 - The component props
 * @param root0.searchQuery - Current search query value
 * @param root0.onSearchQueryChange - Callback when search query changes
 * @param root0.canCreateNew - Whether a new tag can be created with current search query
 * @param root0.onCreateFromSearch - Callback to create tag from search query
 */
const TagSelectorSearchSectionContainer = ({
  searchQuery,
  onSearchQueryChange,
  canCreateNew,
  onCreateFromSearch,
}: TagSelectorSearchSectionProps) => (
  <Stack className="gap-3">
    <Input
      variant="outline"
      size="md"
      className="h-9 rounded-sm border-outline-strong data-[focus=true]:border-outline-strong"
      leadingIcon={Search}
      leadingIconTone="muted"
      field={{
        testID: "tag-selector-search-input",
        placeholder: "Search or create a tag",
        value: searchQuery,
        onChangeText: onSearchQueryChange,
        "aria-label": "Search or create a tag",
      }}
    />

    {/* Create new tag from search */}
    {canCreateNew && (
      <Button
        label={`Create "${searchQuery.trim()}"`}
        variant="solid"
        tone="accent"
        size="sm"
        icon={Plus}
        iconPosition="leading"
        onPress={onCreateFromSearch}
        accessibilityLabel={`Create tag ${searchQuery.trim()}`}
        className="self-start"
      />
    )}
  </Stack>
);

export default TagSelectorSearchSectionContainer;
